import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Injectable, signal, WritableSignal, inject } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, throwError } from 'rxjs';
import { catchError, delay, filter, finalize, switchMap, take } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { MethodApi } from 'app/api/api-request';
import { Router } from '@angular/router';
import { LoginService } from 'app/services/data/login.service';
import { ConnectivityService } from './connectivity.service';

@Injectable()
export class ErrorHandlerInterceptor implements HttpInterceptor {
    private translate = inject(TranslateService);
    private toastr = inject(ToastrService);
    private loginService = inject(LoginService);
    private router = inject(Router);
    private api = inject(MethodApi);
    private connectivityService = inject(ConnectivityService);

    private refreshTokenInProgress: boolean = false;
    private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    private isOnline: WritableSignal<boolean> = signal(true);
    private onlineChanges$: Observable<boolean>;

    constructor() {
        this.onlineChanges$ = this.connectivityService.isOnline();
        this.onlineChanges$.subscribe(status => {
            this.isOnline.set(status);
        });
    }
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            catchError((error: HttpErrorResponse) => {
                if (!this.isOnline()) {
                    // Attendre que l'application revienne en ligne
                    return this.onlineChanges$.pipe(
                        filter(status => status === true),
                        take(1),
                        delay(1000), // attendre 1 seconde après retour en ligne (optionnel)
                        switchMap(() => next.handle(req))
                    );
                }
                return this.handleHttpError(error, req, next);
            })
        );
    }
    private handleHttpError(errorResponse: HttpErrorResponse, req: HttpRequest<any>, next: HttpHandler) {
        if (errorResponse.status === 400) {
            this.showErrorToast();
        } else if (errorResponse.status === 401) {
            return this.handleUnauthorizedResponse(req, next);
        } else if (errorResponse.status === 403) {
            this.toastr.error(
                this.translate.instant('COMMON.TOAST.ACCESS_DENIED'),
                this.translate.instant('COMMON.TOAST.ERROR_TITLE')
            );
        } else if (errorResponse.status === 429) {
            this.toastr.error(
                this.translate.instant('COMMON.TOAST.API_REQUEST_EXCEEDED'),
                this.translate.instant('COMMON.TOAST.ERROR_TITLE')
            );
        } else {
            this.showErrorToast();
        }

        return throwError(errorResponse);
    }
    private showErrorToast() {
        this.toastr.error(
            this.translate.instant('COMMON.TOAST.ERROR_API_TEXT'),
            this.translate.instant('COMMON.TOAST.ERROR_TITLE')
        );
    }

    private handleUnauthorizedResponse(req: HttpRequest<any>, next: HttpHandler) {
        // 401 errors are most likely going to be because we have an expired token that we need to refresh.
        if (this.refreshTokenInProgress) {
            // If refreshTokenInProgress is true, we will wait until refreshTokenSubject has a non-null value
            // which means the new token is ready and we can retry the request again
            return this.refreshTokenSubject.pipe(
                filter(result => result !== null),
                take(1),
                switchMap(() => next.handle(this.addAuthenticationToken(req)))
            );
        } else {
            this.refreshTokenInProgress = true;

            // Set the refreshTokenSubject to null so that subsequent API calls will wait until the new token has been retrieved
            this.refreshTokenSubject.next(null);
            return this.loginService.reConnect().pipe(
                catchError(() => {
                    this.router.navigateByUrl('/login');
                    return EMPTY;
                }),
                switchMap(() => {
                    this.refreshTokenSubject.next(true);
                    return next.handle(this.addAuthenticationToken(req));
                }),
                // When the call to refreshToken completes we reset the refreshTokenInProgress to false
                // for the next time the token needs to be refreshed
                finalize(() => this.refreshTokenInProgress = false)
            );
        }
    }

    private addAuthenticationToken(request: HttpRequest<any>): HttpRequest<any> {
        return request.clone({
            headers: request.headers.set('authorization', this.api.setAuthorization())
        });
    }
}
